import { withStorageSync } from '@angular-architects/ngrx-toolkit';
import { computed } from '@angular/core';
import { withSelectedEntity } from '@cf/core';
import { patchState, signalStore, type, withComputed, withMethods, withState } from '@ngrx/signals';
import type { EntityId } from '@ngrx/signals/entities';
import {
  addEntity,
  removeAllEntities,
  removeEntity,
  setAllEntities,
  withEntities,
} from '@ngrx/signals/entities';
import type { Organization, OrganizationInvitations } from '../types';

export interface OrgInvitationNotification {
  type: 'invitation';
}
export interface OrgTentativeEventNotification {
  org_id: number;
  type: 'tentative_event';
}
export type OrgNotification = OrgInvitationNotification | OrgTentativeEventNotification;

type OrganizationsState = {
  actAsOrganization: EntityId | null;
  notifications: OrgNotification[];
};

export const INVITATIONS_COLLECTION = 'invitations';

export const selectFn = ({ entityMap, ids, selectedEntityId }: any) => ({
  entityMap,
  ids,
  selectedEntityId,
});

export const OrganizationsStore = signalStore(
  { providedIn: 'root' },
  withState<OrganizationsState>({
    actAsOrganization: null,
    notifications: [],
  }),
  withEntities<Organization>(),
  withSelectedEntity(),
  withEntities({
    entity: type<OrganizationInvitations>(),
    collection: 'invitations',
  }),
  withComputed((store) => ({
    hasAnyNotifications: computed(() => {
      return store.notifications().length > 0;
    }),
    hasCurrentOrgNotifications: computed(() =>
      store
        .notifications()
        .some(
          (notification) =>
            'org_id' in notification &&
            (notification.org_id === store.actAsOrganization() ||
              notification.org_id === store.selectedEntityId()),
        ),
    ),
    orgNotifications: computed(() =>
      store.notifications().reduce(
        (acc, curr) => {
          if ('org_id' in curr) {
            acc[curr.org_id] = curr;
          }
          return acc;
        },
        {} as Record<number, OrgNotification | undefined>,
      ),
    ),
  })),
  withMethods((store) => ({
    setActAsOrganization: (organizationId: EntityId | null) => {
      patchState(store, { actAsOrganization: organizationId });
    },
    setOrganizations: (organizations: Organization[]) => {
      patchState(store, setAllEntities(organizations));

      const selectedOrganization = store.selectedEntity();

      if (!selectedOrganization || !store.ids().includes(selectedOrganization.id)) {
        store.selectEntity(store.entities()[0]?.id ?? null);
      }
    },
    addOrganization: (organization: Organization) => {
      patchState(store, addEntity(organization));
    },
    removeOrganization: (organizationId: EntityId) => {
      const selectedEntityId = store.selectedEntityId();
      patchState(store, removeEntity(organizationId));
      if (selectedEntityId === organizationId) {
        if (store.entities().length > 0) {
          store.selectEntity(store.entities()[0].id);
        } else {
          store.clearSelectedEntity();
        }
      }
    },
    setInvitations: (invitations: OrganizationInvitations[]) => {
      patchState(store, setAllEntities(invitations, { collection: INVITATIONS_COLLECTION }));
    },
    setNotifications: (notifications: OrgNotification[]) => {
      patchState(store, { notifications });
    },
    reset: () => {
      patchState(
        store,
        removeAllEntities(),
        removeAllEntities({ collection: INVITATIONS_COLLECTION }),
        {
          notifications: [],
          actAsOrganization: null,
        },
      );
      store.clearSelectedEntity();
    },
  })),
  withStorageSync({
    key: 'organizations',
    storage: () => localStorage,
    select: selectFn,
  }),
);

export type OrganizationsStore = InstanceType<typeof OrganizationsStore>;
